import { useState, useMemo, useEffect } from 'react'
import { buildStarterSlots as buildSlots, SLOT_LABELS, SLOT_LABELS_SHORT } from '../../lib/rosterSlots'
import { applyMove, toSlotAssignments } from '../../lib/lineupMoves'
import MovePlayerSheet from './MovePlayerSheet'
import PositionBadge from './PositionBadge'
import { useFantasyRoster, useSetFantasyLineup, useDropRosterPlayer, useResolveIneligibleIr, useFantasyTrades, useRespondToTrade, useBlurbPlayerIds, useFantasySettings, useGlobalRank, useFantasyLineupHistory, useFantasyWeeklyLineup, useSetFantasyWeeklyLineup, useFantasyWeekProjections, useFantasyRosterUpdates } from '../../hooks/useLeagues'
import { useAuth } from '../../hooks/useAuth'
import { SkeletonRows, SkeletonBlock } from '../ui/Skeleton'
import { toast } from '../ui/Toast'
import Avatar from '../ui/Avatar'
import PlayerDetailModal from './PlayerDetailModal'
import FantasyGlobalRankModal from './FantasyGlobalRankModal'
import { ProposeTradeModal } from './FantasyTrades'
import BlurbDot, { markBlurbSeen } from './BlurbDot'
import RosterUpdatesSheet from './RosterUpdatesSheet'
import { useReadState, READ_KINDS } from '../../hooks/useReadState'
import InjuryBadge from '../ui/InjuryBadge'
import { TradeDropModal } from './FantasyTrades'
import { isIrEligible } from '../../lib/injuryStatus'

// Explicit past-tense map — templating `${action}ed` gives "declineed" and
// `${action}d` gives "canceld" / "vetod". Hand-mapping avoids both traps
// and keeps future action names honest.
function pastTense(action) {
  return {
    accept: 'accepted',
    decline: 'declined',
    veto: 'vetoed',
    cancel: 'canceled',
  }[action] || `${action}d`
}

// InjuryBadge + colors moved to ui/InjuryBadge.jsx — local copy
// was case-sensitive and missed PUP/SUS.

// Build the starter slot list from the league's roster_slots config so leagues
// with non-default lineups (e.g., 2 WR instead of 3) don't render extra empty
// slots that nobody can fill. Slot keys must match what the BE writes
// (qb, rb1..rbN, wr1..wrN, te, flex, superflex, k, def).
// Shared definition — lib/rosterSlots. Was an inline copy that drifted from
// the server's starterPlan; see that module's header for the bugs it caused.
function buildStarterSlots(rosterSlots) {
  return buildSlots(rosterSlots).map((s) => ({ key: s.key, label: s.label, positions: s.positions }))
}

const POSITION_STAT_CONFIG = {
  QB: [
    { key: 'pass_yd', label: 'PYD', comma: true },
    { key: 'pass_td', label: 'PTD' },
    { key: 'pass_int', label: 'INT' },
    { key: 'rush_att', label: 'CAR' },
    { key: 'rush_yd', label: 'RYD' },
  ],
  RB: [
    { key: 'rush_att', label: 'CAR' },
    { key: 'rush_yd', label: 'RYD', comma: true },
    { key: 'rush_td', label: 'RTD' },
    { key: 'rec', label: 'REC' },
    { key: 'rec_yd', label: 'REYD' },
    { key: 'rec_td', label: 'RETD' },
    { key: 'rec_tgt', label: 'TGT' },
  ],
  WR: [
    { key: 'rec', label: 'REC' },
    { key: 'rec_yd', label: 'REYD', comma: true },
    { key: 'rec_td', label: 'RETD' },
    { key: 'rec_tgt', label: 'TGT' },
  ],
  TE: [
    { key: 'rec', label: 'REC' },
    { key: 'rec_yd', label: 'REYD', comma: true },
    { key: 'rec_td', label: 'RETD' },
    { key: 'rec_tgt', label: 'TGT' },
  ],
  K: [
    { key: 'fgm', label: 'FGM' },
    { key: 'fgm_50_plus', label: '50+' },
    { key: 'fgmiss', label: 'MISS' },
    { key: 'xpm', label: 'XPM' },
  ],
  DEF: [
    { key: 'def_sack', label: 'SCK' },
    { key: 'def_int', label: 'INT' },
    { key: 'def_fum_rec', label: 'FR' },
    { key: 'def_td', label: 'DTD' },
  ],
}

// IDP stat lines — Sleeper's nfl_players.position codes for defenders
// are granular (DE / DT / NT / LB / ILB / OLB / MLB / CB / DB / S / FS
// / SS). Map each family to the same stat template so the display is
// consistent regardless of which specific position code is on file.
const IDP_STAT_TEMPLATE = [
  // Leads the line: a defensive TD is the single biggest thing an IDP can
  // do and it was missing here entirely. Sleeper sends it as idp_def_td and
  // the sync folds it into the def_td column (see sleeperService.js).
  { key: 'def_td', label: 'TD' },
  { key: 'idp_tkl_solo', label: 'SOLO' },
  { key: 'idp_tkl_ast', label: 'AST' },
  { key: 'idp_tkl_loss', label: 'TFL' },
  { key: 'idp_sack', label: 'SCK' },
  { key: 'idp_int', label: 'INT' },
  { key: 'idp_pass_def', label: 'PD' },
  { key: 'idp_ff', label: 'FF' },
  { key: 'idp_fum_rec', label: 'FR' },
]
for (const pos of ['DL', 'DE', 'DT', 'NT', 'LB', 'ILB', 'OLB', 'MLB', 'DB', 'CB', 'S', 'FS', 'SS']) {
  POSITION_STAT_CONFIG[pos] = IDP_STAT_TEMPLATE
}

function formatSeasonStats(position, stats) {
  if (!stats || !position) return null
  // Dual positions (e.g. "LB/DL"): use the first part's stat template
  // since both IDP families share the same IDP_STAT_TEMPLATE anyway.
  // For hybrid non-IDP (rare), first-part wins.
  const lookupKey = position.includes('/') ? position.split('/')[0].trim() : position
  const config = POSITION_STAT_CONFIG[lookupKey]
  if (!config) return null
  // Only render stat entries with a non-zero value — a row of "117 RYD ·
  // 0 RTD · 0 REC · 0 REYD" reads as noise when only the rushing yards
  // are meaningful. Keep the order from the config so what does show
  // reads in the same canonical sequence.
  const nonZero = config.filter((c) => (Number(stats[c.key]) || 0) > 0)

  // Two-way players. Travis Hunter's position arrives as "WR/DB", and the
  // dual-position rule above takes the FIRST part — so he got the WR
  // template and the tackle he actually recorded (and scores for) was
  // dropped. His player modal showed it while his roster row did not.
  // Any defensive production by a player whose template isn't the IDP one
  // gets appended, same as FantasyMatchup / FantasyLiveView already do.
  //
  // Note this is specifically why first-part-wins isn't enough for hybrids:
  // the half of his line that the template discards is real scoring.
  //
  // Excluded: IDP positions (the template IS the IDP one, so appending
  // would duplicate every stat) and DEF, which is a team D/ST row whose
  // numbers are the unit's, not one player's.
  const isIdpTemplate = config === IDP_STAT_TEMPLATE
  const extras = (isIdpTemplate || lookupKey === 'DEF')
    ? []
    : IDP_STAT_TEMPLATE.filter((c) => (Number(stats[c.key]) || 0) > 0)

  const shown = [...nonZero, ...extras]
  if (shown.length === 0) return null
  return shown.map((c) => {
    const val = Number(stats[c.key]) || 0
    return `${c.comma ? val.toLocaleString() : val} ${c.label}`
  }).join(' · ')
}

// InjuryBadge imported at top of file (see ui/InjuryBadge.jsx).

// Kickoff prefix for a roster row's game line: "Sun 1PM ", or "" once the
// game is under way (the time has stopped being useful then, and Live view is
// where you go for a score).
//
// Formatted from the UTC instant in the VIEWER's timezone — the server sends
// UTC precisely because it can't know where the reader is. Mirrors the format
// FantasyMatchup already uses, including dropping ":00" on the hour so
// "Sun 1PM @ CHI" fits a phone row where "Sun 1:00PM @ CHI" ellipsised.
function kickoffLabel(row) {
  const iso = row?.current_week_starts_at
  if (!iso) return ''
  if (row?.current_week_game_status === 'live' || row?.current_week_game_status === 'final') return ''
  const d = new Date(iso)
  if (isNaN(d)) return ''
  const day = d.toLocaleDateString(undefined, { weekday: 'short' })
  const onTheHour = d.getMinutes() === 0
  const time = d
    .toLocaleTimeString(undefined, onTheHour ? { hour: 'numeric' } : { hour: 'numeric', minute: '2-digit' })
    .replace(' ', '')
  return `${day} ${time} `
}

function PlayerRow({ row, slotLabel, onMove, onViewDetail, blurbIds }) {
  // The badge carries the kickoff lock: once a player's game has started the
  // server refuses to move him, so his badge loses its ring and stops being a
  // button rather than staying tappable and failing on save.
  //
  // Exception mirrors the server's — a move between two NON-scoring slots
  // (bench <-> IR) can't change what the lineup scored, so a locked bench
  // player keeps his ring. buildMoveOptions then offers only IR.
  const inNonScoringSlot = row?.slot === 'bench' || row?.slot === 'ir'
  const lockedForMoves = row?.is_locked === true && !inNonScoringSlot

  // The badge sits OUTSIDE the row button rather than inside it: nested
  // buttons are invalid, and the badge has to stay tappable on a locked row
  // where the rest of the row still opens the player card.
  return (
    <div className="relative">
      <div className="w-full flex items-center gap-2 px-2 py-3 rounded-lg border border-text-primary/10 bg-bg-primary/40 transition-colors">
        <PositionBadge
          label={slotLabel}
          locked={lockedForMoves}
          onTap={lockedForMoves || !onMove ? undefined : onMove}
          title={lockedForMoves ? 'Game has started — this player is locked' : undefined}
        />
        <button
          type="button"
          onClick={() => onViewDetail?.(row?.player_id)}
          className="flex-1 min-w-0 flex items-center gap-3 text-left rounded-lg hover:bg-bg-card-hover transition-colors px-1 py-1"
        >
        {row?.nfl_players?.headshot_url && (
          <img
            src={row.nfl_players.headshot_url}
            alt=""
            className="w-11 h-11 rounded-full object-cover bg-bg-secondary shrink-0"
            onError={(e) => { e.target.style.display = 'none' }}
          />
        )}
        <div className="flex-1 min-w-0 md:flex-none md:basis-[30%] md:max-w-[30%]">
          <div className="flex items-center gap-1.5">
            <span className="text-base font-bold text-text-primary truncate">{row?.nfl_players?.full_name || 'Empty'}</span>
            <InjuryBadge status={row?.nfl_players?.injury_status} />
            {blurbIds && <BlurbDot playerId={row?.player_id} blurbIds={blurbIds} />}
          </div>
          <div className="text-xs text-text-primary">
            {row?.nfl_players?.position} · {row?.nfl_players?.team || 'FA'}
            {row?.current_week_opponent ? (
              <span className="text-text-muted ml-2">
                {kickoffLabel(row)}{row.current_week_is_home ? 'vs' : '@'} {row.current_week_opponent}
              </span>
            ) : ('current_week_opponent' in (row || {}) && row?.nfl_players?.team) ? (
              <span className="text-yellow-400 ml-2 font-semibold">BYE</span>
            ) : null}
          </div>
        </div>
        {/* Stat line — desktop only. Always the week being viewed.
            Both sources now agree on the field name: the live roster sends
            week_stats for the current week, and the lineup-history snapshot
            sends week_stats for a past one. It used to call the past-week
            version `season_stats`, and the live roster ALSO had a
            season_stats holding a real full-season aggregate — so past weeks
            silently read the aggregate and every one of them looked
            identical.

            The header above used to read "Season Total", which this
            column has never shown — on the current week it renders
            week_stats, and before kickoff those are empty, so the label
            sat over a blank column promising something else. It reads
            "Week N" now. Real season totals live in the player detail
            modal, which sums every stat across played weeks in a sticky
            Total row and is the better home for them anyway.
            Sits in a flex-grow column with text-left so it reads
            close to the player name (not jammed against the points
            column on the right). Header above the table mirrors this
            with a "Season Total" label aligned to the same column. */}
        {row?.nfl_players?.position && (() => {
          const source = row.week_stats
          if (!source) return null
          const statLine = formatSeasonStats(row.nfl_players.position, source)
          if (!statLine) return null
          return (
            <div className="hidden md:block md:flex-1 md:min-w-0 md:ml-3 md:mr-3">
              <div className="text-sm text-text-secondary tabular-nums truncate">{statLine}</div>
            </div>
          )
        })()}
        {row?.nfl_players && (() => {
          // Two-line layout: actual points (or — when player hasn't
          // played) on top, projection below in muted small text.
          // Mirrors the Matchup view's mental model so the user's eye
          // learns one pattern instead of two — and a Thursday-final
          // score never reads as a Monday projection at a glance.
          // hasPlayed = the player has a stats row for this week, even
          // if their actual score is 0 (real DEF/K zeros stay distinct
          // from "game hasn't kicked off yet").
          const hasPlayed = row.week_stats != null
          const showProj = row.weekly_projection != null
          return (
            <div className="text-right shrink-0 mr-1 ml-auto">
              {hasPlayed ? (
                <div className="text-lg font-display tabular-nums text-white leading-none">{(row.live_points ?? 0).toFixed(1)}</div>
              ) : (
                <div className="text-lg font-display tabular-nums text-text-muted leading-none">—</div>
              )}
              {showProj && (
                <div className="text-xs tabular-nums text-text-secondary mt-1">
                  Proj {row.weekly_projection.toFixed(1)}
                </div>
              )}
            </div>
          )
        })()}
        </button>
      </div>
    </div>
  )
}

function EmptySlot({ slotLabel, onTap, canFill = true, emptyHint }) {
  // An empty spot is a tap target in its own right: tapping it lists the
  // players eligible to fill it, which is the same move as tapping one of
  // those players and choosing this spot. Either route, one write.
  //
  // The badge carries a ring here for the same reason it does on a filled
  // row — it marks the thing you can act on.
  const label = emptyHint || `Empty ${slotLabel}`
  if (!canFill) {
    return (
      <div className="w-full flex items-center gap-2 px-2 py-2.5 rounded-lg border border-dashed border-text-primary/20 bg-bg-primary/40">
        <PositionBadge label={slotLabel} locked />
        <div className="flex-1 text-xs text-text-muted">{label}</div>
      </div>
    )
  }
  return (
    <button
      type="button"
      onClick={onTap}
      className="w-full flex items-center gap-2 px-2 py-2.5 rounded-lg border border-dashed border-text-primary/20 bg-bg-primary/40 hover:bg-bg-card-hover hover:border-accent/40 transition-colors text-left"
    >
      <PositionBadge label={slotLabel} />
      <div className="flex-1 text-xs text-text-muted">{label} — tap to fill</div>
    </button>
  )
}

export default function FantasyMyTeam({ league }) {
  const { profile } = useAuth()
  const { data: fantasySettings } = useFantasySettings(league.id)
  const STARTER_SLOTS = useMemo(() => buildStarterSlots(fantasySettings?.roster_slots), [fantasySettings?.roster_slots])
  const currentWeek = fantasySettings?.current_week || 1
  const season = fantasySettings?.season || 2026
  const totalWeeks = 17
  const [viewWeek, setViewWeek] = useState(null) // null = current week
  const activeWeek = viewWeek ?? currentWeek
  const isCurrentWeek = activeWeek === currentWeek
  const isPastWeek = activeWeek < currentWeek
  const isFutureWeek = activeWeek > currentWeek

  const { data: rawRoster, isLoading } = useFantasyRoster(league.id)
  const { data: historyData } = useFantasyLineupHistory(league.id, isPastWeek ? activeWeek : null, isPastWeek ? season : null)
  const { data: weeklyLineupData } = useFantasyWeeklyLineup(league.id, isFutureWeek ? activeWeek : null)
  const setWeeklyLineup = useSetFantasyWeeklyLineup(league.id)
  const { data: trades } = useFantasyTrades(league.id)
  const { data: blurbIdsList } = useBlurbPlayerIds(league.id)
  const blurbIds = useMemo(
    () => new Map((blurbIdsList || []).map((r) => [r.player_id, r.latest_id])),
    [blurbIdsList],
  )
  // Same source the row dots read, so the pill's badge can never disagree
  // with the dots sitting right below it.
  const blurbReadState = useReadState()
  // Updates sheet. The content fetch is deferred until the sheet is opened —
  // the dots themselves only need blurbIds, which is already cached.
  const [showUpdates, setShowUpdates] = useState(false)
  const { data: rosterUpdates, isLoading: updatesLoading } = useFantasyRosterUpdates(league.id, showUpdates)
  const respond = useRespondToTrade(league.id)
  const setLineup = useSetFantasyLineup(league.id)
  const dropPlayer = useDropRosterPlayer(league.id)
  const [confirmDrop, setConfirmDrop] = useState(null) // roster row being dropped
  // Tap-to-move. The anchor is whichever side was tapped — a player, or an
  // empty spot — and the sheet lists everything legal on the other side.
  // There is no draft state: a pick saves immediately.
  const [moveAnchor, setMoveAnchor] = useState(null)
  // Optimistic slots applied while a save is in flight, so the row moves the
  // instant you tap. Cleared when the refetch lands or the save fails.
  const [pendingSlots, setPendingSlots] = useState(null)
  const [savingMove, setSavingMove] = useState(false)
  const [showCounterTrade, setShowCounterTrade] = useState(null)
  const [detailPlayerId, setDetailPlayerId] = useState(null)
  const [showGlobalRank, setShowGlobalRank] = useState(false)
  const { data: globalRankData } = useGlobalRank(league.id)
  const hasGlobalRank = globalRankData?.status === 'ok' && globalRankData?.format?.team_count > 1
  const [expandedTradeId, setExpandedTradeId] = useState(null)
  const [tradeAcceptedModal, setTradeAcceptedModal] = useState(false)
  // { tradeId, trade, dropsNeeded } — opens the drop picker when the server
  // says accepting this trade would put the receiver over the roster cap.
  const [tradeDropModal, setTradeDropModal] = useState(null)
  // Ineligible-IR resolution. Holds the names the server said must come off
  // IR, so the drop picker can explain WHY it's asking.
  const [irDropModal, setIrDropModal] = useState(null)
  const resolveIr = useResolveIneligibleIr(league.id)

  // Reset move state when navigating weeks
  useEffect(() => {
    setMoveAnchor(null)
    setPendingSlots(null)
  }, [activeWeek])

  // For past weeks, use lineup history; for future weeks with saved lineup, use that; otherwise current roster
  // Fetch per-week projection + opponent context whenever the user
  // is viewing a non-current week so we can overlay onto the roster
  // rows (the downstream renderer reads weekly_projection +
  // current_week_opponent directly off each row).
  const { data: weekContextData } = useFantasyWeekProjections(league.id, !isCurrentWeek ? activeWeek : null)
  const weekProjMap = weekContextData?.projections
  const weekOppMap = weekContextData?.opponents
  function applyWeekOverlay(arr) {
    if (!arr || !weekContextData) return arr
    return arr.map((r) => {
      const overlay = {}
      const team = r.nfl_players?.team
      const isOnBye = team && weekOppMap && !weekOppMap[team]
      // Bye-week players: force projection to 0. Sleeper occasionally
      // returns a non-zero projection on a bye week (their model
      // doesn't always cross-reference the schedule), which would
      // otherwise show "23.8 PROJ" for a guy who's not playing.
      if (isOnBye) {
        overlay.weekly_projection = 0
      } else {
        const wp = weekProjMap?.[r.player_id]
        if (wp != null) overlay.weekly_projection = wp
      }
      if (team && weekOppMap) {
        const op = weekOppMap[team]
        if (op) {
          overlay.current_week_opponent = op.opponent
          overlay.current_week_is_home = op.is_home
          // Carried through with the opponent, never separately — a row must
          // not pair one week's opponent with another week's kickoff.
          overlay.current_week_starts_at = op.starts_at || null
          overlay.current_week_game_status = op.game_status || null
        } else {
          // Null opponent + the present-but-null current_week_opponent
          // field triggers BYE label in PlayerRow.
          overlay.current_week_opponent = null
          overlay.current_week_is_home = null
          overlay.current_week_starts_at = null
          overlay.current_week_game_status = null
        }
      }
      return Object.keys(overlay).length > 0 ? { ...r, ...overlay } : r
    })
  }
  const serverRoster = useMemo(() => applyWeekOverlay(rawRoster), [rawRoster, weekContextData])
  // While a move is in flight the tapped rows sit where the user put them.
  // Dropped as soon as the refetch returns — comparing against the server's
  // own answer rather than a timer, so a rejected move snaps back rather than
  // lingering as a lie.
  const roster = useMemo(() => {
    if (!pendingSlots) return serverRoster
    return serverRoster.map((r) => (
      pendingSlots[r.player_id] && pendingSlots[r.player_id] !== r.slot
        ? { ...r, slot: pendingSlots[r.player_id] }
        : r
    ))
  }, [serverRoster, pendingSlots])

  // Pill visibility + badge. Derived from blurbIds and read state, which are
  // already loaded for the row dots, so neither costs an extra request — the
  // badge is correct before the sheet's content has ever been fetched.
  const rosterBlurbState = useMemo(() => {
    const seen = blurbReadState[READ_KINDS.BLURB] || {}
    let total = 0
    let unread = 0
    // `roster` is undefined until the fetch lands: applyWeekOverlay returns
    // its argument untouched when falsy, so serverRoster — and therefore
    // roster — is undefined on the first render. The pre-existing uses are
    // both behind a pendingSlots guard and never saw it; this memo runs
    // immediately, and iterating undefined threw "re is not iterable" on
    // every My Team load.
    for (const r of roster || []) {
      const latestId = blurbIds.get(r.player_id)
      if (!latestId) continue
      total++
      if (seen[r.player_id] !== latestId) unread++
    }
    return { total, unread }
  }, [roster, blurbIds, blurbReadState])
  const rosterHasAnyBlurb = rosterBlurbState.total > 0
  const unreadUpdateCount = rosterBlurbState.unread

  // The server has caught up — stop overriding it.
  useEffect(() => {
    if (!pendingSlots) return
    const settled = serverRoster.every((r) => pendingSlots[r.player_id] === r.slot)
    if (settled) setPendingSlots(null)
  }, [serverRoster, pendingSlots])

  // Players sitting on IR who have healed. They block every roster move, so
  // this is surfaced up front rather than waiting for the manager to hit the
  // wall on an add, drop or trade and have to work out what it meant.
  const ineligibleIrPlayers = useMemo(
    () => (roster || []).filter((r) => r.slot === 'ir' && !isIrEligible(r.nfl_players?.injury_status)),
    [roster],
  )


  const weeklyRoster = weeklyLineupData?.roster
  const hasWeeklyLineup = isFutureWeek && weeklyRoster && weeklyRoster.length > 0
  function openPlayerDetail(playerId) {
    if (playerId) markBlurbSeen(playerId, blurbIds.get(playerId))
    setDetailPlayerId(playerId)
  }

  // Any pending trade involving me — incoming (I'm the receiver) or
  // outgoing (I proposed and am waiting). Counters back-and-forth keep
  // the latest trade pending so the banner stays visible the whole time.
  const pendingTrades = (trades || []).filter((t) => t.status === 'pending' && (t.receiver_user_id === profile?.id || t.proposer_user_id === profile?.id))

  // Build a working slot-by-player map (server slot or draftSlots override)
  // For future weeks with a saved weekly lineup, use those slots as the base
  const slotByPlayer = useMemo(() => {
    if (!roster) return {}
    const map = {}
    // Start from weekly lineup if viewing a future week with saved data
    if (hasWeeklyLineup) {
      // Initialize all current roster players as bench
      for (const r of roster) map[r.player_id] = 'bench'
      // Override with weekly lineup slots (only players still on roster)
      for (const w of weeklyRoster) {
        if (map[w.player_id] !== undefined) map[w.player_id] = w.slot
      }
    } else {
      for (const r of roster) map[r.player_id] = r.slot
    }
    return map
  }, [roster, hasWeeklyLineup, weeklyRoster])


  if (isLoading) return (
    <div className="space-y-4">
      <div className="rounded-xl border border-text-primary/20 overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <SkeletonBlock className="h-4 w-32" />
        </div>
        <div className="p-3">
          <SkeletonRows count={10} imgSize="w-9 h-9" />
        </div>
      </div>
      <div className="rounded-xl border border-text-primary/20 overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <SkeletonBlock className="h-4 w-24" />
        </div>
        <div className="p-3">
          <SkeletonRows count={5} imgSize="w-9 h-9" />
        </div>
      </div>
    </div>
  )

  const hasRoster = roster && roster.length > 0
  const isSalaryCap = fantasySettings?.format === 'salary_cap'
  if (!hasRoster) {
    const slots = fantasySettings?.roster_slots || (isSalaryCap
      ? { qb: 1, rb: 2, wr: 3, te: 1, flex: 1, k: 1, def: 1 }
      : { qb: 1, rb: 2, wr: 2, te: 1, flex: 1, k: 1, def: 1, bench: 6 })
    const starterSlots = []
    // Mirror buildStarterSlots order so pre-draft empty view matches
    // the post-draft layout (including IDP families when configured).
    const slotExpansion = { qb: 'QB', rb: 'RB', wr: 'WR', te: 'TE', flex: 'FLEX', superflex: 'SFLEX', k: 'K', def: 'DEF', dl: 'DL', lb: 'LB', db: 'DB', s: 'S' }
    for (const [key, label] of Object.entries(slotExpansion)) {
      for (let i = 0; i < (slots[key] || 0); i++) starterSlots.push(label)
    }
    const benchCount = isSalaryCap ? 0 : (slots.bench || 6)
    const irCount = isSalaryCap ? 0 : (slots.ir || 0)
    return (
      <div className="space-y-4">
        <p className="text-sm text-text-secondary text-center">
          {isSalaryCap ? 'Set your lineup each week under the salary cap.' : "You'll see your team here after the draft!"}
        </p>
        <div className="rounded-xl border border-text-primary/20 overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <h3 className="text-base font-semibold text-text-primary">{isSalaryCap ? 'Lineup' : 'Starters'}</h3>
          </div>
          <div className="divide-y divide-text-primary/10">
            {starterSlots.map((label, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2.5">
                <span className="text-xs font-bold text-text-muted w-8 shrink-0">{label}</span>
                <div className="w-9 h-9 rounded-full border border-text-primary/20 shrink-0" />
                <div className="flex-1 text-xs text-text-muted italic">Empty</div>
              </div>
            ))}
          </div>
        </div>
        {benchCount > 0 && (
        <div className="rounded-xl border border-text-primary/20 overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <h3 className="text-base font-semibold text-text-primary">Bench</h3>
          </div>
          <div className="divide-y divide-text-primary/10">
            {Array.from({ length: benchCount }, (_, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2.5">
                <span className="text-xs font-bold text-text-muted w-8 shrink-0">BN</span>
                <div className="w-9 h-9 rounded-full border border-text-primary/20 shrink-0" />
                <div className="flex-1 text-xs text-text-muted italic">Empty</div>
              </div>
            ))}
          </div>
        </div>
        )}
        {irCount > 0 && (
        <div className="rounded-xl border border-text-primary/20 overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <h3 className="text-base font-semibold text-text-primary">IR</h3>
          </div>
          <div className="divide-y divide-text-primary/10">
            {Array.from({ length: irCount }, (_, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2.5">
                <span className="text-xs font-bold text-text-muted w-8 shrink-0">IR</span>
                <div className="w-9 h-9 rounded-full border border-text-primary/20 shrink-0" />
                <div className="flex-1 text-xs text-text-muted italic">Empty</div>
              </div>
            ))}
          </div>
        </div>
        )}
      </div>
    )
  }

  async function handleTradeAction(tradeId, action, dropPlayerIds) {
    try {
      await respond.mutateAsync({ tradeId, action, drop_player_ids: dropPlayerIds })
      if (action === 'accept') {
        setTradeAcceptedModal(true)
        setTradeDropModal(null)
      } else {
        toast(`Trade ${pastTense(action)}`, 'success')
      }
    } catch (err) {
      const body = err?.response || err
      // Accept would put the receiver over the roster cap — open the drop
      // picker inline instead of surfacing a generic error toast. Prior code
      // just toasted, leaving the user stuck without a clear path forward
      // (which was the root of the "Trade canceld" screenshot chain — the
      // trade went through anyway with drops silently dropped at review).
      if (body?.requires_drop && action === 'accept') {
        const trade = pendingTrades?.find((t) => t.id === tradeId)
        if (trade) {
          setTradeDropModal({ tradeId, trade, dropsNeeded: body.drops_needed || 1 })
          return
        }
      }
      toast(err.message || `Failed to ${action} trade`, 'error')
    }
  }

  // A past week renders from its lineup SNAPSHOT, not from today's roster.
  //
  // historyData was already being fetched for past weeks but only fed the
  // win/loss banner — the player rows always came off the live roster. That
  // roster carried a full-season aggregate, so every past
  // week showed the identical stat line. It also carries TODAY's slots, so a
  // player benched since would appear benched in a week he started.
  //
  // The history rows are already shaped for PlayerRow (see the comment on
  // /dfs/lineup-history) and their `week_stats` is that week's line.
  const usingHistory = isPastWeek && historyData?.roster?.length > 0
  const displayRoster = usingHistory ? applyWeekOverlay(historyData.roster) : roster

  // Group roster by current (working) slot
  const playersBySlot = {}
  for (const r of displayRoster) {
    // Snapshot rows carry the slot the player actually held that week.
    // slotByPlayer is built from the live roster and wouldn't know about a
    // player who has since been dropped.
    const slot = usingHistory ? r.slot : slotByPlayer[r.player_id]
    if (!playersBySlot[slot]) playersBySlot[slot] = []
    playersBySlot[slot].push(r)
  }
  const benchPlayers = playersBySlot.bench || []
  const benchSlots = fantasySettings?.roster_slots?.bench || 6
  // Past weeks are a record, not a lineup — their rows render without any
  // move affordance at all.
  const canEditLineup = isCurrentWeek || isFutureWeek
  const emptyBenchCount = Math.max(0, benchSlots - benchPlayers.length)
  const irPlayers = playersBySlot.ir || []
  const irSlotCount = fantasySettings?.roster_slots?.ir || 0
  const emptyIrCount = Math.max(0, irSlotCount - irPlayers.length)

  // A healed player on IR blocks every roster move, and clearing him needs
  // roster room — which needs a drop, which is itself blocked. The server
  // resolves both in one call; this asks first so the manager chooses who goes.
  async function handleResolveIr(dropPlayerId = null) {
    try {
      const res = await resolveIr.mutateAsync(dropPlayerId)
      setIrDropModal(null)
      const moved = (res?.moved || []).join(' and ')
      toast(
        res?.dropped
          ? `${moved} moved to your bench. Dropped ${res.dropped}.`
          : `${moved} moved to your bench.`,
        'success',
      )
    } catch (err) {
      const body = err.body || err.response || {}
      if (body.needs_drop) {
        setIrDropModal({ names: body.ineligible_ir_players || [] })
        return
      }
      toast(err.message || 'Could not clear IR', 'error')
    }
  }

  // One tap = one committed transaction.
  //
  // The full slot map goes every time, not a delta: both setters validate the
  // whole lineup, so a swap can never land half-written with two players in
  // one slot. Optimistic state moves the row immediately and is rolled back
  // if the server disagrees.
  async function handleMovePick(option) {
    const anchor = moveAnchor
    if (!anchor || savingMove) return
    const nextSlots = applyMove({ anchor, option, roster })

    setSavingMove(true)
    setPendingSlots(nextSlots)
    setMoveAnchor(null)
    try {
      const assignments = toSlotAssignments(nextSlots)
      const res = isFutureWeek
        ? await setWeeklyLineup.mutateAsync({ week: activeWeek, slots: assignments })
        : await setLineup.mutateAsync(assignments)

      // The server refuses moves for players whose game has kicked off. The
      // sheet shouldn't have offered one, but say so plainly if it did rather
      // than leaving a row that appears to have moved.
      const refused = res?.skipped_locked || []
      if (refused.length) {
        setPendingSlots(null)
        const names = refused.map((r) => r.full_name).join(', ')
        toast(`${names} ${refused.length > 1 ? 'have' : 'has'} already played and can't be moved`, 'error')
      } else {
        const moved = roster.find((r) => r.player_id === (anchor.type === 'player' ? anchor.playerId : option.player?.player_id))
        const name = moved?.nfl_players?.full_name || 'Player'
        const dest = option.kind === 'fill'
          ? (STARTER_SLOTS.find((x) => x.key === option.slotKey)?.label || option.label)
          : option.label
        toast(`${name} moved to ${dest}`, 'success')
      }
    } catch (err) {
      setPendingSlots(null)
      toast(err.message || 'Could not move that player', 'error')
    } finally {
      setSavingMove(false)
    }
  }



  return (
    <div className="space-y-4">
      {ineligibleIrPlayers.length > 0 && (
        <div className="rounded-xl border border-incorrect/40 bg-incorrect/10 px-4 py-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-text-primary">
              {ineligibleIrPlayers.map((r) => r.nfl_players?.full_name).join(' and ')}
              {ineligibleIrPlayers.length === 1 ? ' is' : ' are'} no longer eligible for IR
            </div>
            <div className="text-xs text-text-secondary mt-0.5">
              {/* Names the blocked actions AND the remedy. "Roster moves are
                  blocked" was wrong once drops were allowed, and never
                  mentioned that the button beside it resolves this — which
                  is how a manager ended up messaging Nick instead of
                  tapping it. */}
              Tap Clear IR to move {ineligibleIrPlayers.length === 1 ? 'him' : 'them'} to your bench — it offers a drop if your bench is full.
              Until then you can drop players, but not add, claim or trade.
            </div>
          </div>
          <button
            onClick={() => handleResolveIr(null)}
            disabled={resolveIr.isPending}
            className="shrink-0 rounded-lg border-2 border-accent text-accent bg-accent/5 hover:bg-accent/10 px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50"
          >
            {resolveIr.isPending ? 'Clearing…' : 'Clear IR'}
          </button>
        </div>
      )}

      {/* Week navigator */}
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={() => setViewWeek(Math.max(1, activeWeek - 1))}
          disabled={activeWeek <= 1}
          className="text-text-muted hover:text-text-primary disabled:opacity-30 transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
        <div className="text-center">
          <span className="font-display text-lg text-text-primary">Week {activeWeek}</span>
          {isCurrentWeek && <span className="text-xs text-accent ml-2">Current</span>}
          {isPastWeek && <span className="text-xs text-text-muted ml-2">Final</span>}
          {isFutureWeek && hasWeeklyLineup && <span className="text-xs text-correct ml-2">Set</span>}
          {isFutureWeek && !hasWeeklyLineup && <span className="text-xs text-text-muted ml-2">Upcoming</span>}
        </div>
        <button
          onClick={() => setViewWeek(Math.min(totalWeeks, activeWeek + 1))}
          disabled={activeWeek >= totalWeeks}
          className="text-text-muted hover:text-text-primary disabled:opacity-30 transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
      </div>

      {/* Past week: win/loss result + total */}
      {isPastWeek && historyData?.roster?.length > 0 && (
        <div className="text-center text-sm">
          {historyData.matchup_result === 'win' ? (
            <span className="text-correct font-semibold">You won Week {activeWeek}</span>
          ) : historyData.matchup_result === 'loss' ? (
            <span className="text-incorrect font-semibold">You lost Week {activeWeek}</span>
          ) : historyData.matchup_result === 'tie' ? (
            <span className="text-text-muted font-semibold">Week {activeWeek} was a tie</span>
          ) : (
            <span className="text-text-muted">Week {activeWeek} Final</span>
          )}
          {historyData.team_total != null && (
            <span className="ml-2 text-white font-display">{historyData.team_total.toFixed(2)} pts</span>
          )}
        </div>
      )}
      {isPastWeek && (!historyData?.roster?.length) && (
        <div className="text-center text-sm text-text-muted py-8">No lineup history for this week</div>
      )}

      {/* Future week: invalidation warning if players no longer on roster */}
      {hasWeeklyLineup && weeklyRoster.some((w) => w.still_on_roster === false) && (
        <div className="rounded-lg border border-accent/40 bg-accent/10 px-3 py-2 text-xs text-accent text-center">
          Some players in this lineup are no longer on your roster. Edit to fix.
        </div>
      )}

      {/* Pending trade proposals — incoming or outgoing */}
      {pendingTrades.map((trade) => {
        const isOutgoing = trade.proposer_user_id === profile?.id
        const counterparty = isOutgoing
          ? (trade.receiver || trade.receiver_user)
          : (trade.proposer || trade.proposer_user)
        const isExpanded = expandedTradeId === trade.id
        const proposerItems = (trade.fantasy_trade_items || []).filter((i) => i.from_user_id === trade.proposer_user_id)
        const receiverItems = (trade.fantasy_trade_items || []).filter((i) => i.from_user_id === trade.receiver_user_id)
        // From my perspective: what I receive vs what I give up
        const myReceiveItems = isOutgoing ? receiverItems : proposerItems
        const myGiveItems = isOutgoing ? proposerItems : receiverItems
        const counterpartyName = counterparty?.display_name || counterparty?.username
        return (
          <div key={trade.id} className={`rounded-xl border overflow-hidden ${isOutgoing ? 'border-text-primary/20 bg-bg-primary/40' : 'border-accent/40 bg-accent/5'}`}>
            <button
              onClick={() => setExpandedTradeId(isExpanded ? null : trade.id)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left"
            >
              <Avatar user={counterparty} size="sm" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-text-primary">
                  {isOutgoing
                    ? `Waiting on ${counterpartyName} to respond`
                    : `${counterpartyName} proposed a trade`}
                </div>
                <div className="text-[10px] text-text-muted">Tap to review</div>
              </div>
              <svg className={`w-4 h-4 text-text-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isExpanded && (
              <div className="px-4 pb-4">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <div className="text-sm font-semibold text-text-primary mb-2">You receive</div>
                    <div className="space-y-1.5">
                      {myReceiveItems.map((item) => (
                        <div key={item.player_id} className="flex flex-col items-center rounded-lg bg-bg-primary/60 border border-text-primary/10 px-2 py-3 text-center">
                          {item.nfl_players?.headshot_url && <img src={item.nfl_players.headshot_url} alt="" className="w-14 h-14 rounded-full object-cover mb-1.5" />}
                          <div className="text-sm font-bold text-text-primary leading-tight">{item.nfl_players?.full_name}</div>
                          <div className="text-[11px] text-text-muted mt-0.5">{item.nfl_players?.position} · {item.nfl_players?.team}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-text-primary mb-2">You give up</div>
                    <div className="space-y-1.5">
                      {myGiveItems.map((item) => (
                        <div key={item.player_id} className="flex flex-col items-center rounded-lg bg-bg-primary/60 border border-text-primary/10 px-2 py-3 text-center">
                          {item.nfl_players?.headshot_url && <img src={item.nfl_players.headshot_url} alt="" className="w-14 h-14 rounded-full object-cover mb-1.5" />}
                          <div className="text-sm font-bold text-text-primary leading-tight">{item.nfl_players?.full_name}</div>
                          <div className="text-[11px] text-text-muted mt-0.5">{item.nfl_players?.position} · {item.nfl_players?.team}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {trade.message && (
                  <div className="text-sm text-text-primary italic mb-3">"{trade.message}"</div>
                )}
                {isOutgoing ? (
                  <button
                    onClick={() => handleTradeAction(trade.id, 'cancel')}
                    disabled={respond.isPending}
                    className="w-full py-2.5 rounded-lg text-sm font-semibold bg-bg-card text-text-secondary border border-text-primary/20 hover:bg-text-primary/5 transition-colors disabled:opacity-50"
                  >Cancel Trade</button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleTradeAction(trade.id, 'accept')}
                      disabled={respond.isPending}
                      className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-correct text-white hover:bg-correct/90 transition-colors disabled:opacity-50"
                    >Accept</button>
                    <button
                      onClick={() => {
                        setExpandedTradeId(null)
                        setShowCounterTrade(trade)
                      }}
                      disabled={respond.isPending}
                      className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50"
                    >Counter</button>
                    <button
                      onClick={() => handleTradeAction(trade.id, 'decline')}
                      disabled={respond.isPending}
                      className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-incorrect text-white hover:bg-incorrect/90 transition-colors disabled:opacity-50"
                    >Decline</button>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}

      {hasGlobalRank && (
        <button
          onClick={() => setShowGlobalRank(true)}
          className="w-full rounded-xl border border-text-primary/20 bg-bg-primary p-3 flex items-center justify-between hover:bg-bg-secondary transition-colors"
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">🏆</span>
            <div className="text-left">
              <div className="text-sm font-semibold text-text-primary">Global Rank</div>
              <div className="text-xs text-text-primary">See where your team ranks across all IKB leagues with the same roster and scoring settings.</div>
            </div>
          </div>
          <span className="text-text-muted">→</span>
        </button>
      )}

      {showGlobalRank && (
        <FantasyGlobalRankModal leagueId={league.id} onClose={() => setShowGlobalRank(false)} />
      )}

      {tradeAcceptedModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4" onClick={() => setTradeAcceptedModal(false)}>
          <div className="bg-bg-primary border border-text-primary/20 rounded-2xl p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
            <div className="text-3xl mb-3">&#9989;</div>
            <h3 className="font-display text-xl text-text-primary mb-2">Trade Accepted</h3>
            <p className="text-sm text-text-primary leading-relaxed mb-4">
              This trade will be reviewed by the commissioner of this league. Upon approval, the trade will be processed.
            </p>
            <button
              onClick={() => setTradeAcceptedModal(false)}
              className="w-full py-2.5 rounded-xl text-sm font-semibold bg-accent text-white hover:bg-accent-hover transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {irDropModal && (
        <TradeDropModal
          roster={roster}
          trade={null}
          dropsNeeded={1}
          includeIr
          title="Roster is full"
          description={`${irDropModal.names.join(' and ')} ${irDropModal.names.length === 1 ? 'is' : 'are'} no longer eligible for IR and must move to your bench. Choose a player to drop.`}
          confirmLabel="Drop and clear IR"
          pendingLabel="Clearing…"
          isPending={resolveIr.isPending}
          onCancel={() => setIrDropModal(null)}
          onConfirm={(dropIds) => handleResolveIr(dropIds?.[0] || null)}
        />
      )}

      {tradeDropModal && (
        <TradeDropModal
          roster={roster}
          trade={tradeDropModal.trade}
          dropsNeeded={tradeDropModal.dropsNeeded}
          onCancel={() => setTradeDropModal(null)}
          isPending={respond.isPending}
          onConfirm={(dropIds) => handleTradeAction(tradeDropModal.tradeId, 'accept', dropIds)}
        />
      )}

      {showCounterTrade && (
        <ProposeTradeModal
          league={league}
          currentUserId={profile?.id}
          initialReceiverId={showCounterTrade.proposer_user_id}
          counteringTradeId={showCounterTrade.id}
          onClose={() => setShowCounterTrade(null)}
        />
      )}

      {/* Mounted only while open: the sheet snapshots unread state in its
          initial render, so keeping it mounted would freeze that snapshot
          against a roster that has since changed. */}
      {showUpdates && (
        <RosterUpdatesSheet
          updates={rosterUpdates}
          isLoading={updatesLoading}
          onClose={() => setShowUpdates(false)}
        />
      )}

      <div className="rounded-xl border border-text-primary/20 overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center gap-3">
          <h3 className="text-base font-semibold text-text-primary whitespace-nowrap">Starting Lineup</h3>
          {/* Every note on the roster in one place, rather than hunting the
              orange dots row by row. Rendered whenever the roster has any
              note at all — not only when something is unread — so the
              control doesn't appear and vanish between visits. The count
              badge is what carries the urgency. */}
          {rosterHasAnyBlurb && (
            <button
              type="button"
              onClick={() => setShowUpdates(true)}
              className="shrink-0 flex items-center gap-1.5 rounded-full border border-text-primary/40 px-3 py-1 text-xs font-semibold text-text-primary hover:bg-bg-card-hover transition-colors"
            >
              Updates
              {unreadUpdateCount > 0 && (
                <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-white text-[10px] font-bold flex items-center justify-center">
                  {unreadUpdateCount}
                </span>
              )}
            </button>
          )}
          {/* Column label for the stat line PlayerRow renders on desktop.
              Names the week being viewed — the column shows that week's
              stats, never a season total. Hidden in edit mode (the stat
              line is hidden then too). Sits to the left of the Edit
              button so it anchors above where the stats column begins. */}
          <span className="hidden md:inline-block text-xs uppercase tracking-wider text-text-muted ml-auto mr-auto">Week {activeWeek}</span>
          {/* Desktop only. With the Updates pill alongside it there is no room
              for this on a phone — "Starting Lineup" wrapped onto two lines to
              make space for a hint about a gesture that is self-evident the
              first time you try it, and pointless every time after. */}
          {canEditLineup && (
            <span className="hidden md:inline-block text-[10px] text-text-muted">Tap a position to move a player</span>
          )}
        </div>
        <div className="p-3 space-y-1">
          {STARTER_SLOTS.map((slotDef) => {
            const occupant = playersBySlot[slotDef.key]?.[0]
            return (
              <div key={slotDef.key}>
                {occupant ? (
                  <PlayerRow
                    row={occupant}
                    slotLabel={slotDef.label}
                    onMove={canEditLineup ? () => setMoveAnchor({ type: 'player', playerId: occupant.player_id }) : undefined}
                    onViewDetail={openPlayerDetail}
                    blurbIds={blurbIds}
                  />
                ) : (
                  <EmptySlot
                    slotLabel={slotDef.label}
                    canFill={canEditLineup}
                    onTap={() => setMoveAnchor({ type: 'spot', spotId: slotDef.key })}
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div className="rounded-xl border border-text-primary/20 overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-baseline gap-3">
          <h3 className="text-base font-semibold text-text-primary">Bench</h3>
          <span className="hidden md:inline-block text-xs uppercase tracking-wider text-text-muted ml-auto mr-auto">Week {activeWeek}</span>
          <span className="text-xs text-text-muted font-mono ml-auto md:ml-0">{benchPlayers.length}/{benchSlots}</span>
        </div>
        <div className="p-3 space-y-1">
          {benchPlayers.map((r) => (
            <PlayerRow
              key={r.id}
              row={r}
              slotLabel="BN"
              onMove={canEditLineup ? () => setMoveAnchor({ type: 'player', playerId: r.player_id }) : undefined}
              onViewDetail={openPlayerDetail}
              blurbIds={blurbIds}
            />
          ))}
          {Array.from({ length: emptyBenchCount }, (_, i) => (
            <EmptySlot
              key={`bench-empty-${i}`}
              slotLabel="BN"
              canFill={canEditLineup}
              onTap={() => setMoveAnchor({ type: 'spot', spotId: `bench#${benchPlayers.length + i}` })}
            />
          ))}
        </div>
      </div>

      {irSlotCount > 0 && (
        <div className="rounded-xl border border-text-primary/20 overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <h3 className="text-base font-semibold text-text-primary">IR</h3>
          </div>
          <div className="p-3 space-y-1">
            {irPlayers.map((r) => (
              <PlayerRow
                key={r.id}
                row={r}
                slotLabel="IR"
                onMove={canEditLineup ? () => setMoveAnchor({ type: 'player', playerId: r.player_id }) : undefined}
                onViewDetail={openPlayerDetail}
                blurbIds={blurbIds}
              />
            ))}
            {Array.from({ length: emptyIrCount }, (_, i) => (
              <EmptySlot
                key={`ir-empty-${i}`}
                slotLabel="IR"
                canFill={canEditLineup}
                emptyHint="Move an Out or IR player here"
                onTap={() => setMoveAnchor({ type: 'spot', spotId: `ir#${irPlayers.length + i}` })}
              />
            ))}
          </div>
        </div>
      )}


      {moveAnchor && (
        <MovePlayerSheet
          anchor={moveAnchor}
          roster={roster}
          starterSlots={STARTER_SLOTS}
          benchLimit={benchSlots}
          irLimit={irSlotCount}
          isPending={savingMove}
          onPick={handleMovePick}
          onClose={() => setMoveAnchor(null)}
        />
      )}

      {detailPlayerId && (
        <PlayerDetailModal
          leagueId={league.id}
          playerId={detailPlayerId}
          onClose={() => setDetailPlayerId(null)}
          playerContext="my_roster"
          // Same rule the lineup badge uses (see PlayerRow): locked AND in a
          // scoring slot. Bench and IR are droppable while locked.
          dropBlockedReason={(() => {
            const row = roster?.find((r) => r.player_id === detailPlayerId)
            if (!row?.is_locked) return null
            if (row.slot === 'bench' || row.slot === 'ir') return null
            return "His game has already been played and his points are in this week's score. You can drop him once the week finalizes."
          })()}
          onDrop={(pid) => {
            const row = roster?.find((r) => r.player_id === pid)
            if (!row) return
            // Belt and braces — the button is already disabled above, but a
            // stale roster render must not open a confirm dialog for a drop
            // the server is going to refuse.
            if (row.is_locked && row.slot !== 'bench' && row.slot !== 'ir') return
            setDetailPlayerId(null); setConfirmDrop(row)
          }}
        />
      )}

      {confirmDrop && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] md:pb-4" onClick={() => setConfirmDrop(null)}>
          <div className="bg-bg-secondary w-full max-w-md rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg mb-2">Drop {confirmDrop.nfl_players?.full_name}?</h3>
            <p className="text-sm text-text-secondary mb-4">
              Are you sure?
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmDrop(null)}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-bg-card text-text-secondary border border-border hover:bg-bg-card-hover transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    await dropPlayer.mutateAsync(confirmDrop.player_id)
                    toast(`${confirmDrop.nfl_players?.full_name || 'Player'} dropped`, 'success')
                    setConfirmDrop(null)
                  } catch (err) {
                    toast(err.message || 'Failed to drop', 'error')
                  }
                }}
                disabled={dropPlayer.isPending}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-incorrect text-white hover:bg-incorrect/80 transition-colors disabled:opacity-50"
              >
                {dropPlayer.isPending ? 'Dropping…' : 'Drop'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
